import PropTypes from 'prop-types'
import axios from 'axios'
import { kea } from 'kea'
import { put, call } from 'redux-saga/effects'
import isEmpty from 'lodash/isEmpty'
import mapValues from 'lodash/mapValues'
import qs from 'qs'
import DataLogicV2 from 'containers/AppV2/logic'

import { API_BASE_URL } from 'config'
import { csObjectFromUnderscore } from 'utils'

const ENTITIES_LIST_ENDPOINTS = `${API_BASE_URL}/public/entity/`

const DEFAULT_FILTERS = {
  // Tariff filters
  price_list_time_cycle: '',
  // Billing filters
  any_billing: false,
  electronic_billing: false,
  paper_billing: false,
  // Payment filters
  any_payment: false,
  direct_debit: false,
  atm_ctt: false,
  // More filters
  has_additional_restrictions: false,
  contract_duration: false,
  indexed_prices: false,
  is_regulated: false,
  has_refunds_nc: false,
  has_refunds: false,
  green_energy: false,
  entity_designation: [],
  // entity_designation: '',
}

export default kea({
  actions: () => ({
    setFilter: (newFilters) => ({ filter: newFilters }),
    setFromUrl: (filters) => ({ filters }),
    triggerFilter: () => ({}),
    resetFilters: () => ({}),
    setSetterFlag: () => ({}),
    fetchEntities: () => ({}),
    setEntities: (entities) => entities,
    resetEntities: () => ({}),
    setSearchedEntities: (entities) => entities,
    setEntitiesError: (message) => ({ message }),
    searchEntity: (query, entitiesOptions) => ({ query, entitiesOptions }),

    setEntitiesLimit: (limit) => ({ limit }),
    setEntitiesOffset: (offset) => ({ offset }),
    setEntitiesCount: (count) => ({ count }),

    setSearchQuery: (query) => ({ query }),
  }),

  reducers: ({ actions }) => ({
    filters: [
      DEFAULT_FILTERS,
      PropTypes.object,
      {
        [actions.setFilter]: (state, payload) => ({
          ...state,
          ...payload.filter,
        }),
        [actions.setFromUrl]: (state, payload) => ({
          ...state,
          ...payload.filters,
        }),
        [actions.resetFilters]: (state) => ({
          code: state.code,
          ...DEFAULT_FILTERS,
        }),
      },
    ],
    setterFromUrl: [
      false,
      PropTypes.bool,
      {
        [actions.setFromUrl]: () => true,
        [actions.setSetterFlag]: () => true,
      },
    ],
    entitiesOptions: [
      [],
      PropTypes.array,
      {
        [actions.setEntities]: (prevState, payload) => {
          let entitiesOptions = [...prevState]
          Object.values(payload).forEach((entity) => {
            if (entity.is_active) {
              entitiesOptions.push({
                id: entity.id,
                label: entity.designation,
                fieldName: 'entity_designation',
                value: false,
              })
            }
          })
          return entitiesOptions
        },
        [actions.resetEntities]: () => [],
      },
    ],
    searchedEntitiesOptions: [
      [],
      PropTypes.array,
      {
        [actions.setSearchedEntities]: (_, payload) => {
          let entitiesOptions = []
          Object.values(payload).forEach((entity) => {
            if (entity.is_active) {
              entitiesOptions.push({
                id: entity.id,
                label: entity.designation,
                fieldName: 'entity_designation',
                value: false,
              })
            }
          })
          return entitiesOptions
        },
        [actions.resetEntities]: () => [],
      },
    ],
    entitiesError: [
      null,
      PropTypes.string,
      {
        [actions.setEntitiesError]: (state, payload) => payload.message,
      },
    ],
    limit: [
      30,
      PropTypes.number,
      {
        [actions.setEntitiesLimit]: (state, payload) => payload.limit,
      },
    ],
    offset: [
      0,
      PropTypes.number,
      {
        [actions.setEntitiesOffset]: (state, payload) => payload.offset,
      },
    ],
    count: [
      0,
      PropTypes.number,
      {
        [actions.setEntitiesCount]: (state, payload) => payload.count,
      },
    ],
    searchQuery: [
      '',
      PropTypes.string,
      {
        [actions.setSearchQuery]: (state, payload) => payload.query,
      },
    ],
  }),

  takeLatest: ({ actions, workers }) => ({
    [actions.setFilter]: workers.setBrowserHistory,
    [actions.resetFilters]: workers.setBrowserHistory,
    [actions.fetchEntities]: workers.fetchEntities,
    [actions.searchEntity]: workers.searchEntity,
  }),

  start: function*() {
    const { setFromUrl, setSetterFlag, fetchEntities } = this.actions

    /* eslint-enable camelcase */

    const URLQuery = window.location.search.substring(1)
    let filtersfromQuery = qs.parse(URLQuery, { strictNullHandling: true })

    // Parses number/bools fields correctly
    filtersfromQuery = mapValues(filtersfromQuery, (filter, key) => {
      // Converter entities_design de string a array de números
      if (key === 'entity_designation' && typeof filter === 'string') {
        if (filter.includes(',')) {
          return filter.split(',').map((item) => Number(item))
        }
        // Si es un solo número como string
        return [Number(filter)]
      }

      if (filter === 'true') {
        return true
      }

      const convertToNumber = Number(filter)
      // eslint-disable-next-line eqeqeq
      return convertToNumber == filter ? convertToNumber : filter
    })

    yield put(fetchEntities())

    if (!isEmpty(filtersfromQuery)) {
      yield put(setFromUrl(filtersfromQuery))
    } else {
      yield put(setSetterFlag())
    }
  },

  workers: {
    *setBrowserHistory(action) {
      const filters = yield this.get('filters')

      const trueFilters = Object.fromEntries(
        Object.entries(filters)
          .filter(([_, value]) => {
            if (!value) return false
            if (Array.isArray(value) && value.length === 0) return false

            return true
          })
          .map(([key, value]) => {
            if (key === 'entity_designation' && Array.isArray(value)) {
              return [key, value.join(',')]
            }
            return [key, value]
          })
      )

      const query = qs.stringify(trueFilters, { strictNullHandling: true })
      const url = `?${query}`
      window.history.pushState('', '', url)
    },

    *fetchEntities() {
      const limit = yield this.get('limit')
      const offset = yield this.get('offset')

      let rawValues = yield DataLogicV2.get('values')
      let values = Object.assign({}, rawValues)

      const { setEntities, setEntitiesError, setEntitiesCount } = this.actions

      try {
        const response = yield call(axios.get, ENTITIES_LIST_ENDPOINTS, {
          params: {
            limit,
            offset,
            offer_types: values.offerType,
          },
        })

        yield put(setEntities(csObjectFromUnderscore(response.data.results)))
        yield put(setEntitiesCount(response.data.count))
      } catch (error) {
        yield put(setEntitiesError(error))
      }
    },

    *searchEntity({ payload }) {
      const {
        setSearchedEntities,
        setEntitiesError,
        setSearchQuery,
      } = this.actions

      try {
        const response = yield call(axios.get, ENTITIES_LIST_ENDPOINTS, {
          params: {
            designation: payload.query,
          },
        })

        yield put(setSearchQuery(payload.query))
        yield put(
          setSearchedEntities(
            csObjectFromUnderscore(payload.query ? response.data.results : [])
          )
        )
      } catch (error) {
        yield put(setEntitiesError(error))
      }
    },
  },
})



// WEBPACK FOOTER //
// ./src/containers/FiltersSectionV2/logic.js